import type { NANevataSensorObject } from '@/api/models';
import { resolveAlertState, alertStateVariant, isAlertNormal } from '@/lib/enum-resolvers';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { formatDateTime } from '@/lib/formatters';

/**
 * Shared read-only Nevata telemetry display: water-level bar, temperature,
 * pump counters, and configured thresholds. Used by both the read-only
 * Bilge & Float Switch panel and the editable Nevata configuration UI.
 */
export function NevataStatusDisplay({
  nev,
  inNoDischargeZone,
}: {
  nev: NANevataSensorObject;
  inNoDischargeZone?: boolean;
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        {/* Water Level Visual */}
        <div className="flex flex-col items-center">
          {nev.waterLevelValid ? (
            <>
              <div className="relative mb-2 h-32 w-16 overflow-hidden rounded-lg border-2 border-slate-300 bg-slate-100 dark:border-navy-600 dark:bg-navy-800">
                {/* Water fill */}
                <div
                  className="absolute bottom-0 w-full bg-blue-400/70 transition-all duration-500 dark:bg-blue-500/60"
                  style={{
                    height: `${Math.min(100, Math.max(0, (nev.waterLevel / Math.max(nev.criticalLevel, 1)) * 100))}%`,
                  }}
                />
                {/* Level marker lines — only drawn for levels actually
                    reported (non-zero); they come from a full settings refresh. */}
                {nev.bilgeLevel > 0 && (
                  <div
                    className="absolute w-full border-t border-dashed border-yellow-500"
                    style={{
                      bottom: `${Math.min(100, (nev.bilgeLevel / Math.max(nev.criticalLevel, 1)) * 100)}%`,
                    }}
                  />
                )}
                {nev.sirenLevel > 0 && (
                  <div
                    className="absolute w-full border-t border-dashed border-orange-500"
                    style={{
                      bottom: `${Math.min(100, (nev.sirenLevel / Math.max(nev.criticalLevel, 1)) * 100)}%`,
                    }}
                  />
                )}
                {nev.criticalLevel > 0 && (
                  <div className="absolute top-0 w-full border-t-2 border-dashed border-red-500" />
                )}
              </div>
              <p className="text-lg font-bold text-slate-900 dark:text-white">
                {nev.waterLevel.toFixed(1)}"
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Water Level</p>
            </>
          ) : (
            <div className="flex h-32 w-16 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 dark:border-navy-600">
              <span className="text-xs text-slate-400">N/A</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400">Temperature</span>
            <p className="font-medium text-slate-900 dark:text-white">{nev.temperature.toFixed(1)}&deg;F</p>
          </div>
          {!isAlertNormal(nev.freezeAlertState) && (
            <StatusBadge
              label={`Freeze ${resolveAlertState(nev.freezeAlertState)}`}
              variant={alertStateVariant(nev.freezeAlertState)}
              dot
            />
          )}
          {/* Discharge is a device-global setting (No Discharge Zone),
              not a per-sensor value. */}
          <div>
            <span className="text-slate-500 dark:text-slate-400">Discharge</span>
            <p className="font-medium text-slate-900 dark:text-white">
              {inNoDischargeZone ? 'No Discharge Zone' : 'Enabled'}
            </p>
          </div>
        </div>
      </div>

      {/* Pump counters */}
      <div className="mt-4 border-t border-slate-100 pt-3 dark:border-navy-700">
        <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Pump Activity</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-slate-50 p-3 text-center dark:bg-navy-800">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{nev.pumpCounter1}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Pump 1</p>
          </div>
          {nev.numPumps > 1 && (
            <div className="rounded-lg bg-slate-50 p-3 text-center dark:bg-navy-800">
              <p className="text-xl font-bold text-slate-900 dark:text-white">{nev.pumpCounter2}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Pump 2</p>
            </div>
          )}
          <div className="rounded-lg bg-slate-50 p-3 text-center dark:bg-navy-800">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{nev.pumpRuntimeTotal}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Total Runtime (s)</p>
          </div>
        </div>
      </div>

      {/* Level thresholds — bilge/siren/critical levels only arrive in a
          full Nevata settings refresh (not alert/event telemetry), so a 0
          means "not reported." Only show a level that's actually set. */}
      {(nev.bilgeLevel > 0 || nev.sirenLevel > 0 || nev.criticalLevel > 0) && (
        <div className="mt-3 space-y-1 text-xs text-slate-500 dark:text-slate-400">
          {nev.bilgeLevel > 0 && (
            <div className="flex justify-between">
              <span>Bilge Level</span>
              <span>{nev.bilgeLevel.toFixed(1)}"</span>
            </div>
          )}
          {nev.sirenLevel > 0 && (
            <div className="flex justify-between">
              <span>Siren Level</span>
              <span>{nev.sirenLevel.toFixed(1)}"</span>
            </div>
          )}
          {nev.criticalLevel > 0 && (
            <div className="flex justify-between">
              <span>Critical Level</span>
              <span>{nev.criticalLevel.toFixed(1)}"</span>
            </div>
          )}
        </div>
      )}

      {/* Updated */}
      {nev.updatedDT && (
        <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
          Updated: {formatDateTime(nev.updatedDT)}
        </p>
      )}
    </>
  );
}
